
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    Typography,
    Paper,
    TextField,
    Button,
    Box,
    Alert,
    CircularProgress,
    Grid,
    Tabs,
    Tab,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    IconButton,
    Chip,
    Pagination,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Divider,
    useMediaQuery
} from '@mui/material';
import {
    Person as PersonIcon,
    Notifications as NotificationsIcon,
    Settings as SettingsIcon,
    Error as ErrorIcon,
    Warning as WarningIcon,
    Info as InfoIcon,
    CheckCircle as SuccessIcon,
    Delete as DeleteIcon,
    MarkEmailRead as MarkReadIcon,
    ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { useSelector, useDispatch } from 'react-redux';
import { formatDistanceToNow } from 'date-fns';
import { alpha, useTheme } from '@mui/material/styles';
import api from '../../utils/axiosConfig';
import { setUser } from '../../store/authSlice';
import NotificationPreferences from '../Notifications/NotificationPreferences';
import { useNotificationSync } from '../../hooks/useNotificationSync';
import { usePWAStatus } from '../../hooks/usePWAStatus';
import { runtime, sessionStore } from '../../platform';

import BiometricSettings from './BiometricSettings';
import { useAccountDeletionAvailability } from '../../hooks/useAccountDeletionAvailability';

const Profile = ({ biometricStatus, onBiometricStatusChange }) => {
    const accountDeletionAvailable = useAccountDeletionAvailability();
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const user = useSelector((state) => state.auth.user);
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const { isStandalone } = usePWAStatus();
    const isAppShell = runtime.isNative() || isStandalone;
    const { markAsReadSync, markAllAsReadSync, dismissNotificationSync, setupNotificationListener } = useNotificationSync();
    
    const [returnRoute, setReturnRoute] = useState('/dashboard');
    
    // Profile state
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [passwordLoading, setPasswordLoading] = useState(false);
    const [passwordError, setPasswordError] = useState('');
    const [passwordSuccess, setPasswordSuccess] = useState('');
    
    // Notification state
    const [notifications, setNotifications] = useState([]);
    const [notificationsLoading, setNotificationsLoading] = useState(false);
    const [notificationsError, setNotificationsError] = useState(null);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [filter, setFilter] = useState('all');
    const [categoryFilter, setCategoryFilter] = useState('');
    const [unreadCount, setUnreadCount] = useState(0);
    
    // Determine active tab from URL
    const getMainTabValue = () => {
        if (location.pathname.includes('/preferences')) return 2;
        if (location.pathname.includes('/notifications')) return 1;
        return 0; // information tab
    };
    
    const mainTabValue = getMainTabValue();
    const itemsPerPage = 20;
    const tabLabels = [
        isSmallScreen ? 'Info' : 'Information',
        isSmallScreen ? 'Notifications' : 'Notifications',
        isSmallScreen ? 'Preferences' : 'Preferences'
    ];
    const sectionPaperSx = {
        p: { xs: 2, sm: 3 },
        mb: 2.5,
        borderRadius: 2.5,
        border: '1px solid',
        borderColor: 'divider',
        boxShadow: 'none'
    };

    const [profileData, setProfileData] = useState({
        FirstName: '',
        LastName: '',
        Email: ''
    });

    const [passwordData, setPasswordData] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });

    useEffect(() => {
        let active = true;
        void sessionStore.get('lastNonProfileRoute').then((route) => {
            if (active && route) setReturnRoute(route);
        });
        return () => {
            active = false;
        };
    }, []);

    useEffect(() => {
        const fetchUserData = async () => {
            try {
                const response = await api.get('/api/profile');
                setProfileData({
                    FirstName: response.data.FirstName || '',
                    LastName: response.data.LastName || '',
                    Email: response.data.Email || ''
                });
            } catch (error) {
                console.error('Failed to fetch user data:', error);
                // Fallback to user data from Redux store if API fails
                if (user) {
                    setProfileData({
                        FirstName: user.FirstName || user.firstName || '',
                        LastName: user.LastName || user.lastName || '',
                        Email: user.Email || user.email || ''
                    });
                }
            }
        };

        fetchUserData();
    }, [user]);

    // Fetch notifications when on notifications tab
    useEffect(() => {
        if (mainTabValue === 1) {
            fetchNotifications();
        }
    }, [mainTabValue, page, filter, categoryFilter]);

    // Set up listener for notification updates
    useEffect(() => {
        const cleanup = setupNotificationListener(({ eventType, data }) => {
            switch (eventType) {
                case 'markAsRead':
                    setNotifications(prev => 
                        prev.map(notif => 
                            notif.id === data.notificationId 
                                ? { ...notif, is_read: true, read_at: new Date().toISOString() }
                                : notif
                        )
                    );
                    setUnreadCount(prev => Math.max(0, prev - 1));
                    break;
                case 'markAllAsRead':
                    setNotifications(prev => 
                        prev.map(notif => ({ 
                            ...notif, 
                            is_read: true, 
                            read_at: new Date().toISOString() 
                        }))
                    );
                    setUnreadCount(0);
                    break;
                case 'dismiss':
                    setNotifications(prev => {
                        const dismissedNotif = prev.find(n => n.id === data.notificationId);
                        if (dismissedNotif && !dismissedNotif.is_read) {
                            setUnreadCount(prevCount => Math.max(0, prevCount - 1));
                        }
                        return prev.filter(notif => notif.id !== data.notificationId);
                    });
                    break;
                default:
                    break;
            }
        });

        return cleanup;
    }, [setupNotificationListener]);

    const fetchNotifications = async () => {
        setNotificationsLoading(true);
        setNotificationsError(null);
        
        try {
            const params = new URLSearchParams({
                page: page - 1,
                limit: itemsPerPage,
            });

            if (filter === 'unread') {
                params.append('unread_only', 'true');
            }

            if (categoryFilter) {
                params.append('category', categoryFilter);
            }

            const response = await api.get(`/api/notifications?${params}`);
            setUnreadCount(response.data.unreadCount);
            setNotifications(response.data.notifications);
            setTotalPages(response.data.pagination.totalPages);
        } catch (error) {
            console.error('Error fetching notifications:', error);
            setNotificationsError('Failed to load notifications');
        } finally {
            setNotificationsLoading(false);
        }
    };

    const handleProfileChange = (e) => {
        const { name, value } = e.target;
        setProfileData({
            ...profileData,
            [name]: value
        });
        // Clear success message when user starts typing
        if (success) setSuccess('');
    };

    const handlePasswordChange = (e) => {
        const { name, value } = e.target;
        setPasswordData({
            ...passwordData,
            [name]: value
        });
        // Clear success message when user starts typing
        if (passwordSuccess) setPasswordSuccess('');
    };

    const handleProfileSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        setSuccess('');

        try {
            await api.put('/api/profile', profileData);
            
            // Update user data in Redux store
            dispatch(setUser({
                ...user,
                FirstName: profileData.FirstName,
                LastName: profileData.LastName,
                Email: profileData.Email,
                // Also maintain lowercase versions for compatibility
                firstName: profileData.FirstName,
                lastName: profileData.LastName,
                email: profileData.Email
            }));

            setSuccess('Profile updated successfully!');
        } catch (error) {
            console.error('Failed to update profile:', error);
            if (error.response && error.response.data && error.response.data.message) {
                setError(error.response.data.message);
            } else {
                setError('An error occurred while updating your profile. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    const handlePasswordSubmit = async (e) => {
        e.preventDefault();
        setPasswordLoading(true);
        setPasswordError('');
        setPasswordSuccess('');

        // Validate passwords match
        if (passwordData.newPassword !== passwordData.confirmPassword) {
            setPasswordError('New passwords do not match.');
            setPasswordLoading(false);
            return;
        }

        // Validate password length
        if (passwordData.newPassword.length < 6) {
            setPasswordError('New password must be at least 6 characters long.');
            setPasswordLoading(false);
            return;
        }

        try {
            await api.put('/api/profile/password', {
                currentPassword: passwordData.currentPassword,
                newPassword: passwordData.newPassword
            });

            setPasswordSuccess('Password updated successfully!');
            setPasswordData({
                currentPassword: '',
                newPassword: '',
                confirmPassword: ''
            });
        } catch (error) {
            console.error('Failed to update password:', error);
            if (error.response && error.response.data && error.response.data.message) {
                setPasswordError(error.response.data.message);
            } else {
                setPasswordError('An error occurred while updating your password. Please try again.');
            }
        } finally {
            setPasswordLoading(false);
        }
    };

    const handleMainTabChange = (event, newValue) => {
        const routes = ['/profile', '/profile/notifications', '/profile/preferences'];
        navigate(routes[newValue]);
    };

    const handleBack = () => {
        // Navigate to the stored return route (last non-profile route)
        navigate(returnRoute);
    };

    const markAsRead = async (notificationId) => {
        await markAsReadSync(notificationId, api);
    };

    const markAllAsRead = async () => {
        if (unreadCount === 0) return;
        await markAllAsReadSync(api);
    };

    const dismissNotification = async (notificationId) => {
        await dismissNotificationSync(notificationId, api);
    };

    const getNotificationIcon = (type) => {
        switch (type) {
            case 'error': return <ErrorIcon color="error" />;
            case 'warning': return <WarningIcon color="warning" />;
            case 'success': return <SuccessIcon color="success" />;
            case 'info':
            default: return <InfoIcon color="info" />;
        }
    };

    const getPriorityColor = (priority) => {
        switch (priority) {
            case 'urgent': return 'error';
            case 'high': return 'warning';
            case 'normal': return 'primary';
            case 'low': return 'default';
            default: return 'default';
        }
    };

    const handlePageChange = (event, newPage) => {
        setPage(newPage);
    };

    const handleFilterChange = (event) => {
        setFilter(event.target.value);
        setPage(1);
    };

    const handleCategoryFilterChange = (event) => {
        setCategoryFilter(event.target.value);
        setPage(1);
    };

    // TabPanel component for organizing content
    const TabPanel = ({ children, value, index, ...other }) => {
        return (
            <div
                role="tabpanel"
                hidden={value !== index}
                id={`profile-tabpanel-${index}`}
                aria-labelledby={`profile-tab-${index}`}
                {...other}
            >
                {value === index && children}
            </div>
        );
    };

    return (
        <Box sx={{ maxWidth: 980, mx: 'auto', px: { xs: 1.25, sm: 2.5 }, pb: { xs: 2, sm: 3 } }}>
            {!isAppShell && (

                    <Box display="flex" alignItems="center" justifyContent="start" gap={1.5} flexWrap="wrap" mb={2}>
                        <Button
                        variant="text"
                        onClick={handleBack}
                        size="small"
                        startIcon={<ArrowBackIcon />}
                        sx={{ minWidth: 'auto' }}
                    >
                        Back
                    </Button>
                        <Typography variant={isSmallScreen ? 'h6' : 'h4'} component="h1" sx={{ fontWeight: 700 }}>
                            Profile Settings
                        </Typography>
                    </Box>

            )}
            

            {/* Main Tabs */}
            <Paper
                sx={{
                    mb: 2,
                    px: 0.5,
                    borderRadius: 2.5,
                    border: '1px solid',
                    borderColor: 'divider',
                    boxShadow: 'none',
                    position: isAppShell ? 'sticky' : 'static',
                    top: isAppShell ? 8 : 'auto',
                    zIndex: isAppShell ? 10 : 'auto',
                    backdropFilter: isAppShell ? 'blur(8px)' : 'none',
                    backgroundColor: (theme) => isAppShell
                        ? alpha(theme.palette.background.paper, 0.93)
                        : theme.palette.background.paper
                }}
            >
                <Tabs
                    value={mainTabValue}
                    onChange={handleMainTabChange}
                    indicatorColor="primary"
                    textColor="primary"
                    variant={isSmallScreen ? 'scrollable' : 'fullWidth'}
                    scrollButtons={isSmallScreen ? 'auto' : false}
                    allowScrollButtonsMobile
                    sx={{
                        minHeight: 52,
                        '& .MuiTabs-flexContainer': {
                            gap: isSmallScreen ? 0.5 : 1,
                            justifyContent: 'space-between'
                        },
                        '& .MuiTabs-indicator': {
                            height: 3,
                            borderRadius: 99
                        }
                    }}
                >
                    <Tab
                        label={tabLabels[0]}
                        icon={<PersonIcon fontSize="small" />}
                        iconPosition={isSmallScreen ? 'top' : 'start'}
                        wrapped
                        sx={{
                            minHeight: 52,
                            minWidth: isSmallScreen ? 88 : 120,
                            px: isSmallScreen ? 1 : 1.5,
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: isSmallScreen ? '0.8rem' : '0.9rem'
                        }}
                    />
                    <Tab
                        label={tabLabels[1]}
                        icon={<NotificationsIcon fontSize="small" />}
                        iconPosition={isSmallScreen ? 'top' : 'start'}
                        wrapped
                        sx={{
                            minHeight: 52,
                            minWidth: isSmallScreen ? 88 : 120,
                            px: isSmallScreen ? 1 : 1.5,
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: isSmallScreen ? '0.8rem' : '0.9rem'
                        }}
                    />
                    <Tab
                        label={tabLabels[2]}
                        icon={<SettingsIcon fontSize="small" />}
                        iconPosition={isSmallScreen ? 'top' : 'start'}
                        wrapped
                        sx={{
                            minHeight: 52,
                            minWidth: isSmallScreen ? 88 : 120,
                            px: isSmallScreen ? 1 : 1.5,
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: isSmallScreen ? '0.8rem' : '0.9rem'
                        }}
                    />
                </Tabs>
            </Paper>

            {/* Information Tab */}
            <TabPanel value={mainTabValue} index={0}>
                {/* Profile Information Section */}
                <BiometricSettings status={biometricStatus} onStatusChange={onBiometricStatusChange} />
                <Paper sx={{ ...sectionPaperSx, mb: 2 }}>
                    <Typography variant="h6" component="h2" gutterBottom>
                        Personal Information
                    </Typography>

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {success && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                        {success}
                    </Alert>
                )}

                <Box component="form" onSubmit={handleProfileSubmit} noValidate>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                margin="normal"
                                required
                                fullWidth
                                id="FirstName"
                                label="First Name"
                                name="FirstName"
                                autoComplete="given-name"
                                value={profileData.FirstName}
                                onChange={handleProfileChange}
                                disabled={loading}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                margin="normal"
                                required
                                fullWidth
                                id="LastName"
                                label="Last Name"
                                name="LastName"
                                autoComplete="family-name"
                                value={profileData.LastName}
                                onChange={handleProfileChange}
                                disabled={loading}
                            />
                        </Grid>
                    </Grid>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField
                                margin="normal"
                                required
                                fullWidth
                                id="Email"
                                label="Email Address"
                                name="Email"
                                autoComplete="email"
                                type="email"
                                value={profileData.Email}
                                onChange={handleProfileChange}
                                disabled={loading}
                            />
                        </Grid>
                    </Grid>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                        <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            disabled={loading}
                            startIcon={loading ? <CircularProgress size={20} /> : null}
                        >
                            {loading ? 'Updating...' : 'Update Profile'}
                        </Button>
                    </Box>
                </Box>
            </Paper>

                {/* Password Change Section */}
                <Paper sx={sectionPaperSx}>
                    <Typography variant="h6" component="h2" gutterBottom>
                        Change Password
                    </Typography>

                    {passwordError && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {passwordError}
                        </Alert>
                    )}

                    {passwordSuccess && (
                        <Alert severity="success" sx={{ mb: 2 }}>
                            {passwordSuccess}
                        </Alert>
                    )}

                    <Box component="form" onSubmit={handlePasswordSubmit} noValidate>
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            id="currentPassword"
                            label="Current Password"
                            name="currentPassword"
                            type="password"
                            autoComplete="current-password"
                            value={passwordData.currentPassword}
                            onChange={handlePasswordChange}
                            disabled={passwordLoading}
                        />

                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            id="newPassword"
                            label="New Password"
                            name="newPassword"
                            type="password"
                            autoComplete="new-password"
                            value={passwordData.newPassword}
                            onChange={handlePasswordChange}
                            disabled={passwordLoading}
                            helperText="Password must be at least 6 characters long"
                        />

                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            id="confirmPassword"
                            label="Confirm New Password"
                            name="confirmPassword"
                            type="password"
                            autoComplete="new-password"
                            value={passwordData.confirmPassword}
                            onChange={handlePasswordChange}
                            disabled={passwordLoading}
                        />

                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                            <Button
                                type="submit"
                                variant="contained"
                                color="primary"
                                disabled={passwordLoading}
                                startIcon={passwordLoading ? <CircularProgress size={20} /> : null}
                            >
                                {passwordLoading ? 'Updating...' : 'Update Password'}
                            </Button>
                        </Box>
                    </Box>
                </Paper>
            </TabPanel>

            {mainTabValue === 0 && accountDeletionAvailable === true && <Paper sx={sectionPaperSx}>
                <Typography variant="h6" component="h2" gutterBottom>Account access &amp; deletion</Typography>
                <Typography sx={{ mb: 2 }}>Leave a customer’s caregiver list or permanently delete your app account.</Typography>
                <Button variant="outlined" color="error" onClick={() => navigate('/account-deletion')}>
                    Manage account access &amp; deletion
                </Button>
            </Paper>}

            {/* Notifications Tab */}
            <TabPanel value={mainTabValue} index={1}>
                {/* Filters */}
                <Paper sx={{ ...sectionPaperSx, p: { xs: 1.5, sm: 2 }, mb: 2 }}>
                    <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
                        <FormControl size="small" sx={{ minWidth: 120 }}>
                            <InputLabel>Filter</InputLabel>
                            <Select
                                value={filter}
                                label="Filter"
                                onChange={handleFilterChange}
                            >
                                <MenuItem value="all">All</MenuItem>
                                <MenuItem value="unread">Unread Only</MenuItem>
                            </Select>
                        </FormControl>

                        <FormControl size="small" sx={{ minWidth: 120 }}>
                            <InputLabel>Category</InputLabel>
                            <Select
                                value={categoryFilter}
                                label="Category"
                                onChange={handleCategoryFilterChange}
                            >
                                <MenuItem value="">All Categories</MenuItem>
                                <MenuItem value="caregiver">Caregiver</MenuItem>
                                <MenuItem value="alert">Alerts</MenuItem>
                                <MenuItem value="system">System</MenuItem>
                            </Select>
                        </FormControl>

                        <Button
                            variant="outlined"
                            startIcon={<MarkReadIcon />}
                            onClick={markAllAsRead}
                            size="small"
                            disabled={unreadCount === 0}
                        >
                            Mark All Read
                        </Button>
                    </Box>
                </Paper>

                {/* Error State */}
                {notificationsError && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {notificationsError}
                    </Alert>
                )}

                {/* Notifications List */}
                {notificationsLoading && notifications.length === 0 ? (
                    <Box display="flex" justifyContent="center" p={3}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        <Paper
                            sx={{
                                borderRadius: 2.5,
                                border: '1px solid',
                                borderColor: 'divider',
                                boxShadow: 'none'
                            }}
                        >
                            {notifications.length === 0 ? (
                                <Box p={4} textAlign="center">
                                    <NotificationsIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                                    <Typography variant="h6" color="text.secondary">
                                        No notifications found
                                    </Typography>
                                </Box>
                            ) : (
                                <List>
                                    {notifications.map((notification, index) => (
                                        <React.Fragment key={notification.id}>
                                            {index > 0 && <Divider />}
                                            <ListItem
                                                sx={{
                                                    backgroundColor: notification.is_read ? 'transparent' : 'action.hover',
                                                    py: 2,
                                                    alignItems: 'flex-start'
                                                }}
                                            >
                                                <ListItemIcon sx={{ mt: 1 }}>
                                                    {getNotificationIcon(notification.type)}
                                                </ListItemIcon>
                                                
                                                <ListItemText
                                                    primary={
                                                        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                                                            <Typography 
                                                                variant="subtitle1"
                                                                sx={{ 
                                                                    fontWeight: notification.is_read ? 'normal' : 'bold',
                                                                    mr: 1
                                                                }}
                                                            >
                                                                {notification.title}
                                                            </Typography>
                                                            
                                                            <Box display="flex" gap={1} alignItems="center">
                                                                {notification.priority !== 'normal' && (
                                                                    <Chip
                                                                        label={notification.priority.toUpperCase()}
                                                                        size="small"
                                                                        color={getPriorityColor(notification.priority)}
                                                                    />
                                                                )}
                                                                
                                                                {notification.category && (
                                                                    <Chip
                                                                        label={notification.category}
                                                                        size="small"
                                                                        variant="outlined"
                                                                    />
                                                                )}
                                                            </Box>
                                                        </Box>
                                                    }
                                                    secondary={
                                                        <span>
                                                            {notification.message}
                                                            <br />
                                                            <span style={{ fontSize: '0.75rem', color: 'inherit', opacity: 0.6 }}>
                                                                {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
                                                                {Boolean(notification.is_read) && notification.read_at && (
                                                                    <span> • Read {formatDistanceToNow(new Date(notification.read_at), { addSuffix: true })}</span>
                                                                )}
                                                            </span>
                                                        </span>
                                                    }
                                                    secondaryTypographyProps={{
                                                        component: 'div',
                                                        variant: 'body2',
                                                        color: 'text.secondary'
                                                    }}
                                                />
                                                
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                    {!Boolean(notification.is_read) && (
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => markAsRead(notification.id)}
                                                            title="Mark as read"
                                                        >
                                                            <MarkReadIcon fontSize="small" />
                                                        </IconButton>
                                                    )}
                                                    
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => dismissNotification(notification.id)}
                                                        title="Dismiss notification"
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Box>
                                            </ListItem>
                                        </React.Fragment>
                                    ))}
                                </List>
                            )}
                        </Paper>

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <Box display="flex" justifyContent="center" mt={3}>
                                <Pagination
                                    count={totalPages}
                                    page={page}
                                    onChange={handlePageChange}
                                    color="primary"
                                />
                            </Box>
                        )}
                    </>
                )}
            </TabPanel>

            {/* Notification Preferences Tab */}
            <TabPanel value={mainTabValue} index={2}>
                <NotificationPreferences />
            </TabPanel>
        </Box>
    );
};

export default Profile;
